import React, {
  useState,
  useCallback,
  useEffect,
  useRef,
  useMemo,
} from "react";
import { useSelector } from "react-redux";
import { useFlags } from "launchdarkly-react-client-sdk";
import _ from "lodash";

import { ButtonComponent } from "../common/ButtonComponent/Button.component";
import { Checkbox } from "../common/CheckBoxComponent";
import { DropdownComponent } from "../common/DropdownComponent";
import { useOnClickOutside } from "Utils/hooks";
import { getVehiclesUpdateData } from "Slices/vehiclesState";
import { getStopsByCurrentSemanticMapDict } from "Slices/stops";
import { isUnhitchEnable } from "Slices/flags";
import { MissionsModal } from "./MissionsModal";
import { AssignModal } from "./AssignModal";
import { sortCollectionByNumberField } from "Utils/collectionUtils";
import { MISSION_DEPLOYMENT_ACTIONS } from "Utils/constants";
import { FMSLogger } from "FMSLogger";
import { HelperText } from "Components/common/HelperText/HelperText";
import { UnhitchWarning } from "./UnhitchWarning/UnhitchWarning";
import {
  CONFIRMATION_TYPES,
  NON_DURATION_CONFIRMATION_TYPES_DISPLAY_MAPPING,
} from "Components/common/constants";
import {
  updateOrAppendFieldObjInArrayIfFound,
  deepSnakeCaseKeys,
} from "Utils/collectionUtils";

import { ReactComponent as ErrorInfoIcon } from "../common/assets/errorInfoIcon.svg";
import {
  MissionDetailsContainerStyled,
  MissionDetailsLabelStyled,
  MissionDetailsTitleItemStyled,
  MissionDetailsItemStyled,
  InputWrapperStyled,
  InputStyled,
  PlanItemsBodyStyled,
  PlanItemsStyled,
  RemovePlanItemStyled,
  MissionDetailsItemContentStyled,
  TimePlanItemStyled,
  MissionDetailsListStyled,
  ButtonsContainerStyled,
  ErrorMessageStyled,
  StopsDropdownStyled,
  StopsDropdownCurrentStyled,
  StopsDropdownContentStyled,
  PlanItemNameStyled,
  CheckboxBodyStyled,
  HitchTypeStyled,
} from "./styles";

const missionDeploymentActionsLogger = FMSLogger.byPrefix(
  MISSION_DEPLOYMENT_ACTIONS
);

const radioData = [
  {
    label: "Add to queue",
    value: "Continue",
  },
  {
    label: "Overwrite existing",
    value: "Cancel",
  },
];
const cancelPrevious = radioData[1].value;

const getFormattedMissionName = (missionName) =>
  missionName || `Created ${new Date().toLocaleDateString()}`;

const confirmationTypeValues = Object.keys(
  NON_DURATION_CONFIRMATION_TYPES_DISPLAY_MAPPING
);

export const MissionDetails = ({
  editData,
  cancelAction,
  stopsList,
  addPlanMissionAction,
  patchPlanMissionAction,
  deployPlanMissionAction,
  vehicleId,
  vehicleName,
  semanticMapId,
  changeTabAction,
  previousMode = cancelPrevious,
  vdaActionDefinitions,
}) => {
  const { featureFlagVdaAction } = useFlags();
  const isUnhitchFeatureOn = useSelector(isUnhitchEnable);
  const vehiclesUpdateData = useSelector(getVehiclesUpdateData);
  const stopsObject = useSelector(getStopsByCurrentSemanticMapDict);
  const [saveMissionModal, setSaveMissionModal] = useState(false);
  const [assignModal, setAssignModal] = useState(false);
  const [confirmOverwriteMissionData, setConfirmOverwriteMissionData] =
    useState(null);
  const [infinitelyStop, setInfinitelyStop] = useState(true);
  const [isUnhitch, setHitchType] = useState(false);
  const [currentStopItem, setCurrentStopItem] = useState(null);
  const [currentVdaActionItem, setCurrentVdaActionItem] = useState(null);
  const [currentVdaActionItemsToBeAdded, setCurrentVdaActionItemsToBeAdded] =
    useState([]);
  const [stopSeconds, setStopSeconds] = useState("");
  const [missionName, setMissionName] = useState(editData?.data?.name || "");
  const [missionPlanStops, setMissionPlanStops] = useState(
    editData?.data?.stops || []
  );
  const [stopTimeMenuOpened, setStopTimeMenuOpened] = useState(false);
  const [confirmationType, setConfirmationType] = useState(
    confirmationTypeValues[0]
  );
  const [errorMessage, setErrorMessage] = useState("");

  const listRef = useRef(null);
  const dropdownRef = useRef(null);
  const handleClickOutside = useCallback(
    () => setStopTimeMenuOpened(false),
    []
  );
  useOnClickOutside(dropdownRef, handleClickOutside);

  useEffect(() => {
    if (listRef.current && listRef.current.scrollIntoView) {
      listRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [missionPlanStops]);

  const isShowUnhitchWarning = useMemo(() => {
    const isPlanStopsIncludesUnhitch = missionPlanStops
      .map((stop) => stop.hitchType)

      // HitchType.UNHITCH
      .includes(2);

    return isUnhitchFeatureOn && isPlanStopsIncludesUnhitch && isUnhitch;
  }, [isUnhitchFeatureOn, missionPlanStops, isUnhitch]);

  const disabledAddPlanBtn =
    !(stopSeconds || infinitelyStop) ||
    !currentStopItem ||
    isShowUnhitchWarning;

  const addPlanItem = useCallback(() => {
    if (!disabledAddPlanBtn) {
      const { id, name, latitude, longitude } = stopsList.find(
        ({ id }) => id === currentStopItem.id
      );

      setMissionPlanStops([
        ...missionPlanStops,
        {
          id,
          stopPk: id,
          name,
          latitude,
          longitude,
          duration: infinitelyStop ? -1 : stopSeconds,
          order: missionPlanStops.length + 1,
          hitchType: isUnhitch ? 2 : 0,
          confirmation_type: confirmationType,
          // TODO: make the button configurable later
          confirmation_buttons_required: !infinitelyStop
            ? 0
            : confirmationType === confirmationTypeValues[1]
            ? 2
            : confirmationType === confirmationTypeValues[0]
            ? 1
            : 0,
          vda_actions: currentVdaActionItemsToBeAdded,
        },
      ]);
      setInfinitelyStop(true);
      setHitchType(false);
      setStopSeconds("");
      setCurrentStopItem(null);
      setErrorMessage("");
      setConfirmationType(confirmationTypeValues[0]);
      setCurrentVdaActionItem(null);
      setCurrentVdaActionItemsToBeAdded([]);
    } else {
      setErrorMessage(
        "Stop could not be added. Please double check we got your details right."
      );
    }
  }, [
    disabledAddPlanBtn,
    missionPlanStops,
    currentStopItem?.id,
    infinitelyStop,
    stopSeconds,
    isUnhitch,
    confirmationType,
    currentVdaActionItemsToBeAdded,
  ]);

  const removePlanItem = useCallback(
    (indexPlanItem) => {
      const missionPlanStopsItems = [...missionPlanStops];
      // Sort by order first
      const sortedStops = sortCollectionByNumberField(
        missionPlanStopsItems,
        "order"
      );
      // Find the index of the item with the order we want to remove (order = indexPlanItem + 1)
      const indexToRemove = sortedStops.findIndex(
        (stop) => stop.order === indexPlanItem + 1
      );
      if (indexToRemove !== -1) {
        sortedStops.splice(indexToRemove, 1);
        // Reorder remaining items
        const reorderedStops = sortedStops.map((stop, index) => ({
          ...stop,
          order: index + 1,
        }));
        setMissionPlanStops(reorderedStops);
      } else {
        // Log error and maintain current state
        setErrorMessage("Failed to remove stop. Please try again.");
      }
    },
    [missionPlanStops]
  );

  const dataJSON = {
    timeStamp: new Date(),
    previous: previousMode,
    stops: [],
    semanticMap: semanticMapId,
  };

  const openModalAction = () => {
    if (missionPlanStops.length === 0) {
      setErrorMessage(
        "Mission failed to deploy. Double check all mission details are right or contact your admin for support."
      );
      return;
    }
    missionDeploymentActionsLogger.debug("save new mission");
    setErrorMessage("");
    setSaveMissionModal(true);
  };

  const saveAndDeployMission = async (
    pickedId,
    selectedLoopsValue,
    isFleet = false
  ) => {
    const addedMissionData = await addPlanMissionAction({
      ...dataJSON,
      name: getFormattedMissionName(missionName),
      stops: missionPlanStops,
    });
    const currentDeploy = (missionId) => {
      deployPlanMissionAction({
        ...dataJSON,
        name: getFormattedMissionName(missionName),
        ...(isFleet ? { fleet: pickedId } : { vehicle: pickedId }),
        stops: missionPlanStops,
        loops: Number(selectedLoopsValue),
        // This "id" will be changed to "mission" field into fetchDeployMission()
        id: missionId,
      });
      changeTabAction?.();
    };
    if (
      !isFleet &&
      // Because we do not need to show MissionsModal with overwrite confirmation if we
      // are on /missions page (in /missions page previousMode always in "Continue" state)
      previousMode !== "Continue" &&
      vehiclesUpdateData &&
      vehiclesUpdateData[pickedId] &&
      vehiclesUpdateData[pickedId].mission_status === "IN_PROGRESS"
    ) {
      setConfirmOverwriteMissionData({
        text: "This vehicle has a mission in progress. Would you like to cancel the current mission and redeploy this one?",
        confirmAction: () => {
          missionDeploymentActionsLogger.debug("deploy mission with overwrite");
          currentDeploy(addedMissionData.payload.id);
        },
        cancelAction: () => {
          missionDeploymentActionsLogger.debug("cancel mission with overwrite");
          setConfirmOverwriteMissionData(null);
        },
      });
      return;
    }
    missionDeploymentActionsLogger.debug("deploy mission");
    currentDeploy(addedMissionData.payload.id);
  };

  const handleMissionWithPickedVehicle = (vehicleId, selectedLoopsValue) => {
    saveAndDeployMission(vehicleId, selectedLoopsValue);
  };

  const handleMissionWithPickedFleet = (fleetId, selectedLoopsValue) => {
    saveAndDeployMission(fleetId, selectedLoopsValue, true);
  };

  const saveMissionAction = (needDeployMission) => {
    setErrorMessage("");
    if (needDeployMission) {
      setSaveMissionModal(false);
      setAssignModal(true);
    } else {
      addPlanMissionAction({
        ...dataJSON,
        name: getFormattedMissionName(missionName),
        stops: missionPlanStops,
        timeStamp: null,
      }).then(() => {
        // After saving, change tab to refresh the mission list
        changeTabAction?.();
      });
    }
  };

  const editMissionAction = () => {
    missionDeploymentActionsLogger.debug("save edited mission");
    patchPlanMissionAction?.({
      id: editData.data.id,
      data: {
        name: getFormattedMissionName(missionName),
        stops: missionPlanStops.filter((stop) => !stop.deletedAt),
        previous: previousMode,
        timeStamp: editData.data.timeStamp,
      },
    }).then(() => {
      // After editing, change tab to refresh the mission list
      cancelAction();
    });
  };

  const sortedMissionPlanStops = useMemo(
    () =>
      sortCollectionByNumberField(
        missionPlanStops.filter((stop) => !stop.deletedAt),
        "order"
      ),
    [missionPlanStops]
  );

  const HitchStatus = useCallback((hitchType) => {
    //TODO: add HitchType enum
    if (!isUnhitchFeatureOn || hitchType === 0) return null;
    if (hitchType === 1)
      return (
        <HitchTypeStyled>
          <ErrorInfoIcon />
        </HitchTypeStyled>
      );
    if (hitchType === 2) return <HitchTypeStyled>Unhitch</HitchTypeStyled>;
  }, []);

  const currentVdaActionDefinition =
    _.find(
      vdaActionDefinitions,
      (vdaActionDefinition) =>
        vdaActionDefinition.actionType === currentVdaActionItem?.action_type
    ) || null;

  const getCurrentTargetActionParameterValue = useCallback(
    (parameter_definition_key_input) => {
      const parameter_definition_key = _.snakeCase(
        parameter_definition_key_input
      );

      return _.get(
        _.find(
          currentVdaActionItem?.action_parameters,
          (parameter) => parameter[parameter_definition_key]
        ),
        parameter_definition_key,
        ""
      );
    },
    [currentVdaActionItem]
  );

  const isCurrentVdaActionItemReadyToBeAdded = useCallback(() => {
    if (!currentVdaActionItem) return false;
    if (!currentVdaActionItem.action_type) return false;

    //get the list of required action_parameters from currentVdaActionDefinition
    const baseRequiredActionParameterDefinitions = _.pickBy(
      currentVdaActionDefinition?.parameterDefinitions || {},
      (value) => value.isRequired || value.is_required
    );

    //get the list of conditionally required action parameters from currentVdaActionDefinition
    const conditionallyRequiredActionParameterDefinitions = _.pickBy(
      currentVdaActionDefinition?.parameterDefinitions || {},
      (value) => {
        if (
          value?.isRequiredCondition &&
          value?.isRequiredCondition?.parameterKey &&
          value?.isRequiredCondition?.parameterValue
        ) {
          const currentTargetActionParameterValue =
            getCurrentTargetActionParameterValue(
              value?.isRequiredCondition?.parameterKey
            );
          return (
            currentTargetActionParameterValue ===
            value?.isRequiredCondition?.parameterValue
          );
        }
      }
    );

    const mergedActionParameterDefinitions = {
      ...baseRequiredActionParameterDefinitions,
      ...conditionallyRequiredActionParameterDefinitions,
    };

    const requiredActionParameterDefinitions = _.pickBy(
      mergedActionParameterDefinitions,
      (obj) =>
        obj !== undefined &&
        !(obj && Object.keys(obj).length === 0 && obj.constructor === Object)
    );

    //check if all its required action_parameters are filled
    if (_.isEmpty(requiredActionParameterDefinitions)) return true;

    const output = _.every(
      requiredActionParameterDefinitions,
      (parameterDefinitionValue, parameterDefinitionKey) => {
        const currentTargetActionParameterValue =
          getCurrentTargetActionParameterValue(parameterDefinitionKey);
        return currentTargetActionParameterValue !== "";
      }
    );
    return output;
  }, [
    currentVdaActionItem,
    currentVdaActionDefinition,
    getCurrentTargetActionParameterValue,
  ]);

  const addVdaActionItem = useCallback(() => {
    if (isCurrentVdaActionItemReadyToBeAdded()) {
      setCurrentVdaActionItemsToBeAdded([
        ...currentVdaActionItemsToBeAdded,
        {
          action_type: currentVdaActionItem.action_type,
          action_description: currentVdaActionItem.action_description,
          blocking_type: currentVdaActionItem.blocking_type || "HARD",
          action_parameters: currentVdaActionItem.action_parameters || {},
          order: (currentVdaActionItemsToBeAdded?.length || 0) + 1,
        },
      ]);
      setCurrentVdaActionItem(null);
      setErrorMessage("");
    } else {
      setErrorMessage(
        "Action could not be added. Please double check we got your details right."
      );
    }
  }, [
    currentVdaActionItem,
    currentVdaActionItemsToBeAdded,
    isCurrentVdaActionItemReadyToBeAdded,
  ]);

  const removeVdaActionItem = useCallback(
    (indexVdaActionItem) => {
      // Sort by order first
      const sortedVdaActionItems = sortCollectionByNumberField(
        currentVdaActionItemsToBeAdded,
        "order"
      );
      // Find the index of the item with the order we want to remove (order = indexPlanItem + 1)
      const indexToRemove = sortedVdaActionItems.findIndex(
        (vda_action_item) => vda_action_item.order === indexVdaActionItem + 1
      );
      if (indexToRemove !== -1) {
        sortedVdaActionItems.splice(indexToRemove, 1);
        // Reorder remaining items
        const reorderedVdaActionItems = sortedVdaActionItems.map(
          (vda_action_item, index) => ({
            ...vda_action_item,
            order: index + 1,
          })
        );
        setCurrentVdaActionItemsToBeAdded(reorderedVdaActionItems);
      } else {
        // Log error and maintain current state
        setErrorMessage("Failed to remove action. Please try again.");
      }
    },
    [currentVdaActionItemsToBeAdded, setCurrentVdaActionItemsToBeAdded]
  );

  const VdaActionPickerBlock = useMemo(() => {
    const setCurrentVdaActionItemWithActionDefinition = (
      action_definition_obj
    ) => {
      setCurrentVdaActionItem({
        ...currentVdaActionItem,
        action_type: action_definition_obj?.actionType,
        action_description: action_definition_obj?.description,
        blocking_type: action_definition_obj?.blocking_type || "HARD",
        action_parameters: action_definition_obj?.action_parameters || {},
      });
    };

    const setCurrentVdaActionItemActionParameter = (
      parameter_key,
      parameter_value
    ) => {
      const updated_action_parameters = updateOrAppendFieldObjInArrayIfFound(
        currentVdaActionItem?.action_parameters || [],
        (parameter) => parameter[parameter_key],
        parameter_key,
        parameter_value
      );

      setCurrentVdaActionItem({
        ...currentVdaActionItem,
        action_parameters: updated_action_parameters,
      });
    };

    const VdaActionParameterPickerBlock = (
      parameter_definition_key_input,
      parameter_definition_value
    ) => {
      const parameter_definition_key = _.snakeCase(
        parameter_definition_key_input
      );
      const currentTargetActionParameterValue =
        getCurrentTargetActionParameterValue(parameter_definition_key_input);

      switch (parameter_definition_key) {
        case "trigger_type": {
          const setCurrentVdaActionItemActionParameterTriggerType = (
            trigger_type_obj
          ) => {
            setCurrentVdaActionItemActionParameter(
              parameter_definition_key,
              trigger_type_obj?.value
            );
          };

          return (
            <DropdownComponent
              placeholderValue="Trigger Type"
              listData={parameter_definition_value?.valueOptions}
              currentItem={currentTargetActionParameterValue ?? ""}
              changeItemAction={
                setCurrentVdaActionItemActionParameterTriggerType
              }
              targetNameField="value"
              targetIdField="value"
            />
          );
        }
        case "duration": {
          const currentTargetActionParameterTriggerTypeValue = _.get(
            _.find(
              currentVdaActionItem?.action_parameters,
              (parameter) => parameter["trigger_type"]
            ),
            "trigger_type",
            ""
          );
          const shouldShowDurationInput =
            currentTargetActionParameterTriggerTypeValue === "DURATION";
          if (!shouldShowDurationInput) return null;

          return (
            <InputStyled
              type="number"
              placeholder="Duration"
              value={currentTargetActionParameterValue}
              onChange={(e) =>
                setCurrentVdaActionItemActionParameter(
                  parameter_definition_key,
                  e.target.value
                )
              }
              style={{
                width: "120px",
              }}
            />
          );
        }
        default:
          return null;
      }
    };

    const VdaActionParametersPickerBlock = () => {
      return (
        <>
          {Object.entries(
            currentVdaActionDefinition?.parameterDefinitions || {}
          )
            .sort(([, a], [, b]) => (a.order || 0) - (b.order || 0))
            .map(([key, value]) => (
              <div key={key}>{VdaActionParameterPickerBlock(key, value)}</div>
            ))}
        </>
      );
    };

    const CurrentVdaActionItemsToBeAddedBlock = () => {
      if (
        !currentVdaActionItemsToBeAdded ||
        currentVdaActionItemsToBeAdded.length === 0
      )
        return null;

      return (
        <MissionDetailsListStyled>
          {currentVdaActionItemsToBeAdded.map((item, index) => {
            return (
              <PlanItemsBodyStyled key={index} style={{ marginLeft: "20px" }}>
                <PlanItemsStyled>
                  <PlanItemNameStyled>
                    {item.action_description}
                  </PlanItemNameStyled>
                  <PlanItemNameStyled>
                    {JSON.stringify(item.action_parameters)}
                  </PlanItemNameStyled>
                </PlanItemsStyled>
                <RemovePlanItemStyled
                  onClick={() => removeVdaActionItem(index)}
                >
                  remove
                </RemovePlanItemStyled>
              </PlanItemsBodyStyled>
            );
          })}
        </MissionDetailsListStyled>
      );
    };

    return (
      <>
        <CurrentVdaActionItemsToBeAddedBlock />
        <MissionDetailsItemStyled>
          <div style={{ marginLeft: "8px" }}></div>
          <DropdownComponent
            placeholderValue="Select an Action"
            listData={vdaActionDefinitions}
            currentItem={currentVdaActionItem?.action_description ?? ""}
            changeItemAction={setCurrentVdaActionItemWithActionDefinition}
            targetNameField="description"
            targetIdField="actionType"
          />
          <VdaActionParametersPickerBlock />
          <ButtonComponent
            clickAction={addVdaActionItem}
            type={
              !isCurrentVdaActionItemReadyToBeAdded() ? "disabled" : "active"
            }
            width={70}
            fontSize={14}
            marginBottom={0}
          >
            + Action
          </ButtonComponent>
        </MissionDetailsItemStyled>
      </>
    );
  }, [
    vdaActionDefinitions,
    currentVdaActionItem,
    currentStopItem,
    addVdaActionItem,
    currentVdaActionItemsToBeAdded,
    getCurrentTargetActionParameterValue,
    isCurrentVdaActionItemReadyToBeAdded,
  ]); // end of VdaActionPickerBlock

  const VdaActionItemsBlock = ({ vda_actions }) => {
    return (
      <MissionDetailsListStyled>
        {vda_actions.map((item, index) => {
          return (
            <PlanItemsBodyStyled
              key={index}
              style={{ marginLeft: "20px", boxShadow: "none" }}
            >
              <PlanItemsStyled>
                <PlanItemNameStyled>
                  {item.action_description}
                </PlanItemNameStyled>
                <PlanItemNameStyled>
                  {JSON.stringify(item.action_parameters)}
                </PlanItemNameStyled>
              </PlanItemsStyled>
            </PlanItemsBodyStyled>
          );
        })}
      </MissionDetailsListStyled>
    );
  }; // end of VdaActionItemsBlock

  return (
    <MissionDetailsContainerStyled>
      {!confirmOverwriteMissionData && saveMissionModal && (
        <MissionsModal
          text="Would you like deploy the new mission?"
          confirmAction={() => {
            missionDeploymentActionsLogger.debug(
              "save new mission with further deployment"
            );
            saveMissionAction(true);
          }}
          cancelAction={() => {
            missionDeploymentActionsLogger.debug(
              "save new mission without deployment"
            );
            saveMissionAction(false);
          }}
        />
      )}
      {assignModal && (
        <AssignModal
          currentMissionDataStops={missionPlanStops}
          vehicleId={vehicleId}
          vehicleName={vehicleName}
          handleVehiclePick={handleMissionWithPickedVehicle}
          handleFleetPick={handleMissionWithPickedFleet}
          cancelAction={changeTabAction}
        />
      )}
      {confirmOverwriteMissionData && (
        <MissionsModal {...confirmOverwriteMissionData} />
      )}
      {isShowUnhitchWarning && <UnhitchWarning />}
      <MissionDetailsTitleItemStyled>
        <MissionDetailsLabelStyled>Mission Name</MissionDetailsLabelStyled>
      </MissionDetailsTitleItemStyled>
      <InputStyled
        type="text"
        maxLength="25"
        placeholder="Mission Name"
        value={missionName}
        onChange={(e) => {
          setMissionName(e.target.value);
        }}
        style={{
          marginLeft: "0",
          marginRight: "0",
          marginBottom: "8px",
        }}
      />
      <HelperText additionalInfo="Mission names are limited to 25 characters" />
      <MissionDetailsTitleItemStyled>
        <MissionDetailsLabelStyled>Mission steps</MissionDetailsLabelStyled>
      </MissionDetailsTitleItemStyled>
      <MissionDetailsItemContentStyled>
        {sortedMissionPlanStops && sortedMissionPlanStops.length !== 0 && (
          <MissionDetailsListStyled style={{ maxHeight: "500px" }}>
            {sortedMissionPlanStops.map((item, index) => {
              const {
                duration,
                hitchType,
                stopPk,
                confirmation_type,
                confirmationType,
              } = item;
              const name = stopsObject[stopPk]?.name || "No Data";
              // "confirmationType" is the field from GET response while "confirmation_type" is the field for the POST request
              const confirmationTypeKey = confirmationType ?? confirmation_type;

              let {
                vda_actions,
                vdaActions,
                mission_stop_vda_actions,
                missionStopVdaActions,
              } = item;
              vda_actions =
                vda_actions ||
                vdaActions ||
                mission_stop_vda_actions ||
                missionStopVdaActions;
              vda_actions = deepSnakeCaseKeys(vda_actions);

              return (
                <PlanItemsBodyStyled
                  key={`${name}-${index}`}
                  style={{
                    height: `${Math.min(
                      39 + (vda_actions?.length || 0) * 80,
                      500
                    )}px`,
                  }}
                >
                  <MissionDetailsListStyled style={{ maxHeight: "500px" }}>
                    <PlanItemsStyled style={{ height: "80%" }}>
                      <PlanItemNameStyled>{name}</PlanItemNameStyled>
                      <TimePlanItemStyled>
                        {duration !== -1
                          ? `${duration} second${duration > 1 ? "s" : ""}`
                          : confirmationTypeKey
                          ? NON_DURATION_CONFIRMATION_TYPES_DISPLAY_MAPPING[
                              confirmationTypeKey
                            ]
                          : NON_DURATION_CONFIRMATION_TYPES_DISPLAY_MAPPING.CONFIRMATION_TYPE_NONE}
                      </TimePlanItemStyled>
                      {HitchStatus(hitchType)}
                    </PlanItemsStyled>
                    {vda_actions && vda_actions.length > 0 && (
                      <VdaActionItemsBlock vda_actions={vda_actions} />
                    )}
                  </MissionDetailsListStyled>
                  <RemovePlanItemStyled onClick={() => removePlanItem(index)}>
                    remove
                  </RemovePlanItemStyled>
                </PlanItemsBodyStyled>
              );
            })}
            <div ref={listRef}></div>
          </MissionDetailsListStyled>
        )}
        <MissionDetailsItemStyled>
          <DropdownComponent
            placeholderValue="Select a Station"
            listData={stopsList}
            currentItem={currentStopItem?.name ?? ""}
            changeItemAction={setCurrentStopItem}
          />
          <StopsDropdownStyled ref={dropdownRef} innerRef={dropdownRef}>
            <StopsDropdownCurrentStyled
              isOpen={stopTimeMenuOpened}
              onClick={() => setStopTimeMenuOpened(!stopTimeMenuOpened)}
            >
              {!infinitelyStop
                ? `${stopSeconds} second${parseInt(stopSeconds) > 1 ? "s" : ""}`
                : NON_DURATION_CONFIRMATION_TYPES_DISPLAY_MAPPING[
                    confirmationType
                  ]}
            </StopsDropdownCurrentStyled>
            {stopTimeMenuOpened && (
              <StopsDropdownContentStyled>
                <InputWrapperStyled>
                  <Checkbox
                    checked={stopSeconds}
                    onChange={() => {
                      // Do not update the stop seconds if it is already set
                      if (!stopSeconds) {
                        setStopSeconds("1");
                      }
                      setInfinitelyStop(false);
                      setConfirmationType(CONFIRMATION_TYPES.DURATION_RELEASE);
                    }}
                    labelText=""
                  />
                  <InputStyled
                    type="number"
                    min="1"
                    placeholder="countdown (seconds)"
                    value={stopSeconds}
                    onChange={(e) => {
                      setStopSeconds(e.target.value);
                      setInfinitelyStop(false);
                      setConfirmationType(CONFIRMATION_TYPES.DURATION_RELEASE);
                    }}
                    notFocusedItem={true}
                    style={{
                      marginRight: "0",
                      marginBottom: "8px",
                      height: "34px",
                      padding: "10px 0",
                    }}
                  />
                </InputWrapperStyled>
                {confirmationTypeValues.map((key) => {
                  const label =
                    NON_DURATION_CONFIRMATION_TYPES_DISPLAY_MAPPING[key];
                  return (
                    <Checkbox
                      key={label}
                      checked={confirmationType === key}
                      onChange={() => {
                        // Do not update the confirmation type if it is the current selection
                        if (confirmationType !== key) {
                          setInfinitelyStop(true);
                          setStopSeconds("");
                          setConfirmationType(key);
                        }
                      }}
                      labelText={label}
                    />
                  );
                })}
              </StopsDropdownContentStyled>
            )}
          </StopsDropdownStyled>
          {isUnhitchFeatureOn && (
            <CheckboxBodyStyled>
              <Checkbox
                checked={isUnhitch}
                onChange={(event) => {
                  setHitchType(event.target.checked);
                }}
                labelText="Unhitch"
              />
            </CheckboxBodyStyled>
          )}
          <ButtonComponent
            clickAction={addPlanItem}
            type={disabledAddPlanBtn ? "disabled" : "active"}
            width={70}
            fontSize={14}
            marginBottom={0}
          >
            Add
          </ButtonComponent>
        </MissionDetailsItemStyled>
        {featureFlagVdaAction && VdaActionPickerBlock}
      </MissionDetailsItemContentStyled>
      <ButtonsContainerStyled>
        <ButtonComponent
          clickAction={editData ? editMissionAction : openModalAction}
          type={"primary"}
          marginBottom={0}
          posCenter={true}
          fontSize={14}
        >
          {editData ? "Save Changes" : "Create Mission"}
        </ButtonComponent>
        <ButtonComponent
          clickAction={() => {
            missionDeploymentActionsLogger.debug(
              `cancel mission ${editData ? "editing" : "creation"}`
            );
            cancelAction();
          }}
          type="cancel"
          posCenter={true}
          fontSize={14}
          marginBottom={0}
        >
          Cancel
        </ButtonComponent>
      </ButtonsContainerStyled>
      {errorMessage && <ErrorMessageStyled>{errorMessage}</ErrorMessageStyled>}
    </MissionDetailsContainerStyled>
  );
};
